import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Departments mirror the DCF update form (stored as a string id on the user).
const departments = [
  { id: "1", name: "Forest" },
  { id: "2", name: "Wildlife" },
];

// "Add New DCF" (Directory). A DCF is a User with role "DFO", posted to a forest
// division. Mirrors DfoUpdateLayerCustom but creates instead of updates and adds
// the credential + Zone -> Circle -> Division place-of-posting cascade.
const DfosFormPage = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    phoneno: "",
    email: "",
    password: "",
    confirmpassword: "",
    designation: "",
    department: "",
    address: "",
    emergency_contact_name: "",
    emergency_contact_number: "",
    emergency_contact_address: "",
  });

  // Place-of-posting cascade
  const [zones, setZones] = useState([]);
  const [circles, setCircles] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [passwordValid, setPasswordValid] = useState(true);
  const [passwordMatchError, setPasswordMatchError] = useState("");

  // Load zones once.
  useEffect(() => {
    const loadZones = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/backend/generic/forestZone`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setZones(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        console.error("Error fetching forest zones:", err);
        toast.error("Failed to load forest zones.");
      }
    };
    loadZones();
  }, [API_BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "password" || name === "confirmpassword") {
        setPasswordValid(updated.password.length >= 8);
        setPasswordMatchError(
          updated.confirmpassword && updated.password !== updated.confirmpassword
            ? "Passwords do not match."
            : ""
        );
      }
      return updated;
    });
  };

  // Zone -> its circles (each circle carries its divisions).
  const handleZoneChange = async (zoneId) => {
    setSelectedZone(zoneId);
    setSelectedCircle("");
    setSelectedDivision("");
    setCircles([]);
    setDivisions([]);
    if (!zoneId) return;
    const zone = zones.find((z) => z._id === zoneId);
    if (!zone) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/backend/districts/get-circles-and-divisions-by-zone`,
        { zone: zone.name },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (res.data?.status) setCircles(res.data.circles || []);
    } catch (err) {
      console.error("Error fetching circles:", err);
      toast.error("Failed to load circles for the selected zone.");
    }
  };

  const handleCircleChange = (circleId) => {
    setSelectedCircle(circleId);
    setSelectedDivision("");
    const circle = circles.find((c) => c._id === circleId);
    setDivisions(circle?.divisions || []);
  };

  const resetForm = () => {
    setForm({
      firstname: "",
      lastname: "",
      phoneno: "",
      email: "",
      password: "",
      confirmpassword: "",
      designation: "",
      department: "",
      address: "",
      emergency_contact_name: "",
      emergency_contact_number: "",
      emergency_contact_address: "",
    });
    setSelectedZone("");
    setSelectedCircle("");
    setSelectedDivision("");
    setCircles([]);
    setDivisions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const required = [
      "firstname", "lastname", "phoneno", "email", "password",
      "designation", "department", "address",
      "emergency_contact_name", "emergency_contact_number", "emergency_contact_address",
    ];
    const missing = required.filter((f) => !form[f]);
    if (missing.length > 0) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (form.password.length < 8) {
      setPasswordValid(false);
      toast.error("Password must be at least 8 characters long.");
      return;
    }
    if (form.password !== form.confirmpassword) {
      setPasswordMatchError("Passwords do not match.");
      toast.error("Passwords do not match.");
      return;
    }
    const pakPhoneRegex = /^(\+92|0)3[0-9]{9}$/;
    if (!pakPhoneRegex.test(form.phoneno)) {
      toast.error("Please enter a valid Pakistani phone number (e.g., +923001234567 or 03001234567).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!selectedDivision) {
      toast.error("Please select the place of posting (Zone, Circle and Division).");
      return;
    }

    const payload = {
      username: `${form.firstname}_${form.lastname}`.toLowerCase().replace(/\s+/g, "_"),
      first_name: form.firstname.trim(),
      last_name: form.lastname.trim(),
      phone_no: form.phoneno.trim(),
      email: form.email.trim(),
      password: form.password,
      designation: form.designation.trim(),
      role: "DFO", // DCF
      department: form.department,
      address: form.address.trim(),
      emergency_contact_name: form.emergency_contact_name.trim(),
      emergency_contact_number: form.emergency_contact_number.trim(),
      emergency_contact_address: form.emergency_contact_address.trim(),
      forest_zone_id: selectedZone || undefined,
      forest_circle_id: selectedCircle || undefined,
      forest_division_id: selectedDivision || undefined,
    };

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_BASE_URL}/backend/users`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status) {
        toast.success("DCF added successfully!");
        resetForm();
        setTimeout(() => navigate("/dfos-list"), 1200);
      } else {
        toast.error(res.data?.message || "Failed to add DCF.");
      }
    } catch (err) {
      console.error("Add DCF error:", err);
      toast.error(err.response?.data?.message || "An error occurred while adding the DCF.");
    } finally {
      setIsLoading(false);
    }
  };

  const textFields = [
    { label: "First Name", name: "firstname", icon: "mdi:account" },
    { label: "Last Name", name: "lastname", icon: "mdi:account" },
    { label: "Designation", name: "designation", icon: "mdi:badge-account" },
    { label: "Contact No", name: "phoneno", icon: "mdi:phone" },
    { label: "Email", name: "email", icon: "mdi:email", type: "email" },
    { label: "Emergency Contact Name", name: "emergency_contact_name", icon: "mdi:account-alert" },
    { label: "Emergency Contact Number", name: "emergency_contact_number", icon: "mdi:phone-alert" },
    { label: "Emergency Contact Address", name: "emergency_contact_address", icon: "mdi:home-alert" },
  ];

  return (
    <div className="col-lg-12 col-md-12 col-sm-12">
      <ToastContainer />
      <form onSubmit={handleSubmit}>
        <div className="text-center mb-6">
          <h6 className="mb-4 text-3xl font-extrabold text-gray-800 flex items-center justify-center bg-gray-100 p-4 rounded-lg shadow-md">
            <Icon icon="mdi:account-plus" className="text-4xl mx-3 my-1" />
            Add New DCF
          </h6>
        </div>

        <div className="row gy-3">
          {textFields.map(({ label, name, icon, type = "text" }) => (
            <div className="col-lg-4 col-md-6 col-sm-12" key={name}>
              <label className="form-label d-flex align-items-center gap-2">
                <Icon icon={icon} className="text-lg" />
                {label}
              </label>
              <input
                type={type}
                name={name}
                value={form[name]}
                onChange={handleChange}
                className="form-control h-56-px bg-neutral-50 radius-12 shadow-sm"
                placeholder={label}
                required
                disabled={isLoading}
              />
            </div>
          ))}

          {/* Department */}
          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:office-building" className="text-lg" />
              Department
            </label>
            <select
              name="department"
              value={form.department}
              onChange={handleChange}
              className="form-control h-56-px bg-neutral-50 radius-12 d-flex align-items-center shadow-sm"
              required
              disabled={isLoading}
            >
              <option value="">Select Department</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>

          {/* Place of posting: Zone -> Circle -> Division */}
          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:map-marker-radius" className="text-lg" />
              Forest Zone (Place of Posting)
            </label>
            <select
              value={selectedZone}
              onChange={(e) => handleZoneChange(e.target.value)}
              className="form-control h-56-px bg-neutral-50 radius-12 d-flex align-items-center shadow-sm"
              required
              disabled={isLoading}
            >
              <option value="">Select Zone</option>
              {zones.map((z) => (
                <option key={z._id} value={z._id}>{z.name}</option>
              ))}
            </select>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:map-marker-path" className="text-lg" />
              Forest Circle
            </label>
            <select
              value={selectedCircle}
              onChange={(e) => handleCircleChange(e.target.value)}
              className="form-control h-56-px bg-neutral-50 radius-12 d-flex align-items-center shadow-sm"
              required
              disabled={isLoading || !selectedZone}
            >
              <option value="">Select Circle</option>
              {circles.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:map-marker" className="text-lg" />
              Forest Division
            </label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              className="form-control h-56-px bg-neutral-50 radius-12 d-flex align-items-center shadow-sm"
              required
              disabled={isLoading || !selectedCircle}
            >
              <option value="">Select Division</option>
              {divisions.map((d) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>

          {/* Password */}
          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:lock" className="text-lg" />
              Password
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="form-control h-56-px bg-neutral-50 radius-12 shadow-sm"
              placeholder="Enter Password"
              required
              disabled={isLoading}
            />
            {!passwordValid && (
              <span className="text-danger ms-1">Password must be at least 8 characters long.</span>
            )}
          </div>

          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:lock-check" className="text-lg" />
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmpassword"
              value={form.confirmpassword}
              onChange={handleChange}
              className="form-control h-56-px bg-neutral-50 radius-12 shadow-sm"
              placeholder="Confirm Password"
              required
              disabled={isLoading}
            />
            {passwordMatchError && <span className="text-danger ms-1">{passwordMatchError}</span>}
          </div>

          {/* Address */}
          <div className="col-lg-4 col-md-6 col-sm-12">
            <label className="form-label d-flex align-items-center gap-2">
              <Icon icon="mdi:home" className="text-lg" />
              Address
            </label>
            <textarea
              name="address"
              className="form-control bg-neutral-50 radius-12 shadow-sm"
              rows={2}
              placeholder="e.g., House No. 123, Street No. 4, Forest Colony Lahore"
              value={form.address}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            className="btn btn-success text-sm btn-sm px-6 py-12 radius-12 my-24 mx-10 col-lg-3 col-md-6 col-sm-12"
            type="submit"
            disabled={isLoading || !passwordValid || !!passwordMatchError}
          >
            {isLoading ? (
              <span className="d-flex align-items-center justify-content-center">
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Adding...
              </span>
            ) : "Add New DCF"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DfosFormPage;
